import React, { useEffect, useState } from 'react';
import { apiErrorText } from '../lib/apiError';
import { Loader2, RefreshCw, Unplug, ExternalLink, HelpCircle } from 'lucide-react';
import ServiceToggle from './ServiceToggle';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';

const QbIcon = (
  <img src="/images/integrations/quickbooks-mark.png" alt="QuickBooks" className="w-6 h-6" />
);

export const QuickBooksIntegrationCard = ({ orgId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [records, setRecords] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const load = async () => {
    try {
      const r = await apiV2.get(`/organizations/${orgId}/quickbooks/status`);
      setStatus(r.data);
      if (r.data?.connected) {
        const recs = await apiV2.get(`/organizations/${orgId}/quickbooks/sync-records`);
        setRecords(recs.data.records || []);
      }
    } catch (e) {
      setStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const handleConnect = async () => {
    try {
      const r = await apiV2.post(`/organizations/${orgId}/quickbooks/connect`);
      window.location.href = r.data.authUrl;
    } catch (e) {
      toast.error(apiErrorText(e, 'QuickBooks is not configured yet'));
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? Synced records stay in QuickBooks; syncing stops.')) return;
    await apiV2.post(`/organizations/${orgId}/quickbooks/disconnect`);
    toast.success('QuickBooks disconnected');
    setExpanded(false);
    load();
  };

  const handleToggle = async (key, value) => {
    setStatus((s) => ({ ...s, [key]: value }));
    try {
      await apiV2.put(`/organizations/${orgId}/quickbooks/settings`, { [key]: value });
    } catch (e) {
      toast.error('Could not save setting');
      load();
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await apiV2.post(`/organizations/${orgId}/quickbooks/sync`);
      const ex = r.data.expenses, rev = r.data.revenue;
      toast.success(`Synced — expenses: ${ex ? ex.pushed : 0} pushed, revenue days: ${rev ? rev.pushed : 0} pushed`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Sync failed — check connection');
      load();
    } finally {
      setSyncing(false);
    }
  };

  const connected = status?.connected;
  const qboCompanyUrl = status?.environment === 'sandbox'
    ? 'https://app.sandbox.qbo.intuit.com'
    : 'https://qbo.intuit.com';

  return (
    <div className="card-soft p-5 sm:p-6" data-testid="quickbooks-integration-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
          {QbIcon}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="settings-section-title">QuickBooks Online</h3>
            {status?.environment === 'sandbox' && (
              <span className="text-xs font-medium text-gray-400">Sandbox</span>
            )}
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
            ) : connected ? (
              <span className="text-xs font-semibold text-green-600" data-testid="qbo-status-badge">
                Connected{status.companyName ? ` · ${status.companyName}` : ''}
              </span>
            ) : (
              <span className="text-xs font-medium text-gray-400" data-testid="qbo-status-badge">Not connected</span>
            )}
          </div>
          <p className="settings-section-subtitle">
            Keep your books current — expenses post as Purchases, daily booking revenue posts as Sales Receipts
          </p>
        </div>
      </div>

      {!loading && !connected && (
        <button
          onClick={handleConnect}
          data-testid="qbo-connect-btn"
          className="block group"
          aria-label="Connect to QuickBooks"
        >
          <img
            src="/images/integrations/C2QB_green_btn_med_default.svg"
            alt="Connect to QuickBooks"
            className="h-9 w-auto group-hover:hidden"
          />
          <img
            src="/images/integrations/C2QB_green_btn_med_hover.svg"
            alt=""
            aria-hidden="true"
            className="h-9 w-auto hidden group-hover:block"
          />
        </button>
      )}

      {!loading && connected && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <label className="flex items-center gap-2.5 text-sm text-gray-700">
              <ServiceToggle checked={!!status.syncExpenses} onChange={(v) => handleToggle('syncExpenses', v)} data-testid="qbo-sync-expenses-toggle" />
              Sync expenses
            </label>
            <label className="flex items-center gap-2.5 text-sm text-gray-700">
              <ServiceToggle checked={!!status.syncRevenue} onChange={(v) => handleToggle('syncRevenue', v)} data-testid="qbo-sync-revenue-toggle" />
              Sync revenue
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleSync}
              disabled={syncing}
              data-testid="qbo-sync-now-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-white border border-[#2CA01C] px-4 py-2 text-sm font-semibold text-[#2CA01C] hover:bg-green-50 transition-colors disabled:opacity-60"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync now
            </button>
            <button
              onClick={handleDisconnect}
              data-testid="qbo-disconnect-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700 transition-colors"
            >
              <Unplug className="w-4 h-4" /> Disconnect from QuickBooks
            </button>
            {status.lastSyncAt && (
              <span className="text-xs text-gray-400">Last sync {new Date(status.lastSyncAt).toLocaleString()}</span>
            )}
          </div>
          {status.lastSyncError && (
            <div className="space-y-1">
              <p className="text-xs text-red-600" data-testid="qbo-last-sync-error">Last sync error: {status.lastSyncError}</p>
              <a
                href="/support"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="qbo-error-support-link"
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Need help? Open a ticket in the Support Center →
              </a>
            </div>
          )}
          {records.length > 0 && (
            <div>
              <button onClick={() => setExpanded(!expanded)} className="text-xs font-semibold text-gray-500 hover:text-gray-700" data-testid="qbo-recent-toggle">
                {expanded ? 'Hide' : 'Show'} recent sync activity ({records.length})
              </button>
              {expanded && (
                <div className="mt-2 border border-gray-100 rounded-lg divide-y divide-gray-100" data-testid="qbo-sync-records">
                  {records.slice(0, 8).map((r) => (
                    <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${r.status === 'synced' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="font-medium text-gray-700">{r.qboType}</span>
                      <span className="text-gray-500 truncate">{r.bfType === 'revenue_day' ? `Revenue ${r.bfRef}` : `Expense`}</span>
                      <span className="ml-auto font-semibold text-gray-800">${(r.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 space-y-1.5">
        {connected && (
          <a
            href={qboCompanyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="qbo-open-company-link"
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 w-fit"
          >
            <ExternalLink className="w-3 h-3" />
            Open your books in QuickBooks{status?.environment === 'sandbox' ? ' (sandbox)' : ''}
          </a>
        )}
        <button
          onClick={() => setShowGuide(!showGuide)}
          data-testid="qbo-how-it-works-toggle"
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
        >
          <HelpCircle className="w-3 h-3" />
          How the sync works
        </button>
        {showGuide && (
          <div className="mt-2 rounded-lg bg-gray-50 p-4 space-y-2.5 text-xs text-gray-600" data-testid="qbo-how-it-works">
            <p>
              <span className="font-semibold text-gray-800">Expenses</span> — everything you log in
              {' '}<span className="font-medium">Purser → Expenses</span> posts to QuickBooks as a <span className="font-medium">Purchase</span>.
              Find them in QuickBooks under <span className="font-medium">Expenses → Expenses</span> (memo starts with “BookingFlows expense”).
              Categories map to matching accounts in your chart of accounts (Fuel → Fuel, Marketing → Advertising, Rent → Rent or Lease…);
              anything unmatched posts to your default expense account.
            </p>
            <p>
              <span className="font-semibold text-gray-800">Revenue</span> — paid bookings from
              {' '}<span className="font-medium">Purser → Revenue</span> roll up into one <span className="font-medium">Sales Receipt</span> per day,
              posted once the day closes. Find them under <span className="font-medium">Sales → All sales</span>.
            </p>
            <p>
              Nothing duplicates — each expense and each revenue day syncs exactly once. A nightly auto-sync keeps your books
              current, and <span className="font-medium">Sync now</span> pushes the latest anytime.
            </p>
          </div>
        )}
        {!status?.connected && (
          <a
            href="https://quickbooks.intuit.com/partners/affiliates?cid=par_pim_Qxq0keD8r71c"
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="qbo-affiliate-link"
            className="flex items-center gap-1 text-xs font-medium text-[#2CA01C] hover:text-[#108000] w-fit"
          >
            <ExternalLink className="w-3 h-3" />
            Don&apos;t have QuickBooks yet? Get QuickBooks Online &rarr;
          </a>
        )}
        <p className="mt-4 text-[10px] text-gray-400">
          Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
        </p>
      </div>
    </div>
  );
};

export default QuickBooksIntegrationCard;
